import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface Recipient {
  email: string;
  name?: string;
}

export interface ReportSchedule {
  id: string;
  report_id: string;
  organization_id: string;
  created_by: string;
  name: string;
  is_active: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  day_of_week: number | null;
  day_of_month: number | null;
  time_of_day: string;
  timezone: string;
  recipients: Recipient[];
  email_subject: string | null;
  email_intro: string | null;
  include_csv: boolean;
  include_summary: boolean;
  last_sent_at: string | null;
  next_run_at: string | null;
  send_count: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  report?: {
    id: string;
    name: string;
    description: string | null;
  };
}

export interface ReportScheduleLog {
  id: string;
  schedule_id: string;
  status: 'pending' | 'running' | 'sent' | 'failed';
  recipients_count: number | null;
  report_row_count: number | null;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
}

export interface CreateScheduleInput {
  report_id: string;
  organization_id: string;
  name: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  day_of_week?: number;
  day_of_month?: number;
  time_of_day: string;
  timezone?: string;
  recipients: Recipient[];
  email_subject?: string;
  email_intro?: string;
  include_csv?: boolean;
  include_summary?: boolean;
}

export interface UpdateScheduleInput extends Partial<CreateScheduleInput> {
  is_active?: boolean;
}

/**
 * Fetch all report schedules for an organization
 */
export function useReportSchedules(organizationId?: string) {
  return useQuery({
    queryKey: ['report-schedules', organizationId],
    queryFn: async (): Promise<ReportSchedule[]> => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('report_schedules')
        .select(`
          *,
          report:custom_reports(id, name, description)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
  });
}

/**
 * Fetch a single report schedule
 */
export function useReportSchedule(scheduleId?: string) {
  return useQuery({
    queryKey: ['report-schedule', scheduleId],
    queryFn: async (): Promise<ReportSchedule | null> => {
      if (!scheduleId) return null;

      const { data, error } = await supabase
        .from('report_schedules')
        .select(`
          *,
          report:custom_reports(id, name, description)
        `)
        .eq('id', scheduleId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!scheduleId,
  });
}

/**
 * Fetch schedule logs
 */
export function useReportScheduleLogs(scheduleId?: string) {
  return useQuery({
    queryKey: ['report-schedule-logs', scheduleId],
    queryFn: async (): Promise<ReportScheduleLog[]> => {
      if (!scheduleId) return [];

      const { data, error } = await supabase
        .from('report_schedule_logs')
        .select('*')
        .eq('schedule_id', scheduleId)
        .order('started_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data || [];
    },
    enabled: !!scheduleId,
  });
}

/**
 * Create a new report schedule
 */
export function useCreateReportSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateScheduleInput): Promise<ReportSchedule> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('report_schedules')
        .insert({
          ...input,
          created_by: user.id,
        })
        .select(`
          *,
          report:custom_reports(id, name, description)
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['report-schedules', variables.organization_id] });
    },
  });
}

/**
 * Update a report schedule
 */
export function useUpdateReportSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: UpdateScheduleInput & { id: string }): Promise<ReportSchedule> => {
      const { data, error } = await supabase
        .from('report_schedules')
        .update(updates)
        .eq('id', id)
        .select(`
          *,
          report:custom_reports(id, name, description)
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['report-schedules', data.organization_id] });
      queryClient.invalidateQueries({ queryKey: ['report-schedule', data.id] });
    },
  });
}

/**
 * Delete a report schedule
 */
export function useDeleteReportSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, organizationId }: { id: string; organizationId: string }): Promise<void> => {
      const { error } = await supabase
        .from('report_schedules')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['report-schedules', variables.organizationId] });
    },
  });
}

/**
 * Toggle schedule active status
 */
export function useToggleScheduleActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }): Promise<ReportSchedule> => {
      const { data, error } = await supabase
        .from('report_schedules')
        .update({ is_active: isActive })
        .eq('id', id)
        .select(`
          *,
          report:custom_reports(id, name, description)
        `)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['report-schedules', data.organization_id] });
      queryClient.invalidateQueries({ queryKey: ['report-schedule', data.id] });
    },
  });
}

/**
 * Manually trigger a scheduled report (for testing)
 */
export function useTriggerScheduledReport() {
  return useMutation({
    mutationFn: async (scheduleId: string): Promise<void> => {
      const { error } = await supabase.functions.invoke('process-scheduled-reports', {
        body: { scheduleId, manual: true },
      });

      if (error) throw error;
    },
  });
}
